import React, { useEffect, useRef, useContext, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSets,
  fetchSetWords,
  generateSetSignedUrls,
} from "../../redux/slices/wordSetsSlice";
import { Container, Col, Row, Card, Button } from "react-bootstrap";
import {
  CaretLeftFill,
  CaretRightFill,
  Plus,
  Dash,
  JournalText,
  XDiamond,
  XDiamondFill,
} from "react-bootstrap-icons";
import {
  addWordToUserVocabulary,
  hideUserVocabularyWord,
  fetchUserVocabulary,
} from "../../redux/slices/userVocabularySlice";
import "bootstrap/dist/css/bootstrap.min.css";
import "./VocabularyDashboard.css";
import { UserContext } from "../../components/user/UserContext";
import { useNavigate } from "react-router-dom";

export default function VocabularyDashboard() {
  const nightMode = useSelector((state) => state.theme.nightMode); // Access the current night mode state from Redux
  const dispatch = useDispatch();
  const sets = useSelector((state) => state.sets.sets); // Get all sets from the Redux state
  const status = useSelector((state) => state.sets.status); // Get the status of the sets loading process
  const setsSignedUrls = useSelector((state) => state.sets.setsSignedUrls); // Get the signed URLs for the set images
  const userSets = useSelector((state) => state.userVocabulary.userSets); // Get the user's vocabulary sets
  const totalWords = useSelector((state) => state.userVocabulary.totalWords); // Get the user's vocabulary sets
  const newWords = useSelector((state) => state.userVocabulary.newWords); // Get new words
  const learningWords = useSelector(
    (state) => state.userVocabulary.learningWords
  ); // Get words which user learns
  const learnedWords = useSelector(
    (state) => state.userVocabulary.learnedWords
  ); // Get already learned words
  const scrollRef = useRef(null); // Reference for the scrolling wrapper
  const { userAttributes } = useContext(UserContext); // Access user attributes from the context
  const navigate = useNavigate(); // Initialize useNavigate for programmatic navigation
  const [addedSets, setAddedSets] = useState({}); // Track which sets are added to the user's vocabulary

  // Load sets and user vocabulary on component mount
  useEffect(() => {
    const loadData = async () => {
      // Fetch all sets
      const response = await dispatch(fetchSets());
      if (response.payload.sets.length > 0) {
        // Fetch words for the fetched sets
        await dispatch(fetchSetWords(response.payload.sets));
        // Generate signed URLs for the set images
        await dispatch(generateSetSignedUrls(response.payload.sets));
      }

      // Fetch the user's vocabulary to update totalWords in the background
      await dispatch(fetchUserVocabulary({ owner: userAttributes.sub }));
    };

    loadData();
  }, [dispatch, userAttributes.sub]);

  // Update the addedSets state when userSets changes
  useEffect(() => {
    const updatedAddedSets = {};
    // Mark sets that are already in the user's vocabulary
    userSets.forEach((userSet) => {
      updatedAddedSets[userSet.setName] = true;
    });
    setAddedSets(updatedAddedSets); // Update the addedSets state
  }, [userSets]);

  // Show loading message while the sets are being fetched
  if (status === "loading") {
    return <div>Loading...</div>;
  }

  // Function to scroll the sets left
  function scrollLeft() {
    if (scrollRef.current) {
      const cardWidth = scrollRef.current.querySelector(
        ".card-recommended-sets"
      ).offsetWidth;
      scrollRef.current.scrollBy({
        top: 0,
        left: -cardWidth - 10, // Scroll by one card's width (including margin)
        behavior: "smooth",
      });
    }
  }

  // Function to scroll the sets right
  function scrollRight() {
    if (scrollRef.current) {
      const cardWidth = scrollRef.current.querySelector(
        ".card-recommended-sets"
      ).offsetWidth;
      scrollRef.current.scrollBy({
        top: 0,
        left: cardWidth + 10, // Scroll by one card's width (including margin)
        behavior: "smooth",
      });
    }
  }

  // Function to add all words from a set to the user's vocabulary
  function handleAddAllWords(set) {
    if (!set.words || set.words.length === 0) {
      console.log("No words found in this set.");
      return;
    }

    const promises = set.words.map((word, index) => {
      // Dispatch action to add each word to the user's vocabulary
      return dispatch(
        addWordToUserVocabulary({
          word: word.word,
          translation: word.translation,
          owner: userAttributes.sub, // Use actual user ID
          setName: set.setName,
        })
      );
    });

    // After all words are added, update the user's vocabulary state
    Promise.all(promises)
      .then((results) => {
        const errors = results.filter((result) => result.error);
        if (errors.length > 0) {
          console.error("Some words failed to add:", errors);
        } else {
          console.log("All words added successfully.");
          // Refresh the user's vocabulary in the state
          dispatch(fetchUserVocabulary({ owner: userAttributes.sub }));
        }
      })
      .catch((error) => {
        console.error("Error adding words:", error);
      });
  }

  // Function to remove all words from a set in the user's vocabulary
  function handleRemoveAllWords(set) {
    // Ensure userSets is defined and has the expected structure
    if (!userSets || !Array.isArray(userSets)) {
      console.error("userSets is not defined or is not an array.");
      return;
    }

    // Find the corresponding set in userSets
    const wordsToRemove = userSets.find(
      (userSet) => userSet.setName === set.setName
    );

    // Ensure wordsToRemove and its structure is correct
    if (
      !wordsToRemove ||
      !wordsToRemove.words ||
      wordsToRemove.words.length === 0
    ) {
      console.log("No words found in user's vocabulary for this set.");
      return;
    }

    const promises = wordsToRemove.words.map((word, index) => {
      // Dispatch action to remove each word from the user's vocabulary
      return dispatch(hideUserVocabularyWord({ id: word.id }));
    });

    // After all words are removed, update the user's vocabulary state
    Promise.all(promises)
      .then((results) => {
        const errors = results.filter((result) => result.error);
        if (errors.length > 0) {
          console.error("Some words failed to remove:", errors);
        } else {
          console.log("All words removed successfully.");
          // Refresh the user's vocabulary in the state
          dispatch(fetchUserVocabulary({ owner: userAttributes.sub }));
        }
      })
      .catch((error) => {
        console.error("Error removing words:", error);
      });
  }

  const handleViewSetWords = (setName) => {
    navigate(`/vocabulary/${encodeURIComponent(setName)}`);
  };

  return (
    <Container className="mt-4">
      {/* Recommended Sets */}
      <Row className="mb-4">
        <h5>Recommended Sets</h5>
        <Col className="position-relative">
          <div className="scrolling-wrapper flex-nowrap" ref={scrollRef}>
            {sets.map((set) => (
              <Col
                lg={4}
                md={6}
                sm={12}
                xs={12}
                className="d-flex justify-content-center"
                key={set.id}
              >
                <div className="card-recommended-sets">
                  <img
                    src={setsSignedUrls[set.id]}
                    className="card-img-top"
                    alt={set.setName}
                  />
                  <div className="card-img-overlay d-flex flex-column justify-content-end">
                    <button
                      className="plus-button"
                      onClick={
                        () =>
                          addedSets[set.setName]
                            ? handleRemoveAllWords(set) // Remove words if set is already added
                            : handleAddAllWords(set) // Add words if set is not added
                      }
                    >
                      {addedSets[set.setName] ? <Dash /> : <Plus />}
                    </button>
                    <h5 className="card-title text-white">{set.setName}</h5>
                    <p className="card-text text-white">
                      Words set · {set.numberOfWords}{" "}
                      {set.numberOfWords === 1 ? "word" : "words"}
                    </p>
                  </div>
                </div>
              </Col>
            ))}
          </div>
          <div className="scroll-arrows">
            <button className="scroll-arrow" onClick={scrollLeft}>
              <CaretLeftFill />
            </button>
            <button className="scroll-arrow" onClick={scrollRight}>
              <CaretRightFill />
            </button>
          </div>
        </Col>
      </Row>

      {/* My Vocabulary Section */}
      <Row>
        <h5>My Vocabulary</h5>
      </Row>
      <Row className="g-3">
        {/* Vocabulary */}
        <Col md={4}>
          <Card
            className="h-100 d-flex flex-row align-items-center p-3"
            onClick={() => navigate("/vocabulary")}
            style={{ cursor: "pointer" }}
          >
            <JournalText size={48} className="me-3" />
            <div>
              <Card.Title>
                {totalWords} {totalWords === 1 ? "word" : "words"}
              </Card.Title>
              <Card.Text className="text-primary">
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <a href="#">View</a>
              </Card.Text>
            </div>
          </Card>
        </Col>

        {/* Words Statistic */}
        <Col md={8}>
          <Card className="h-100 p-3">
            <Row className="align-items-center">
              <Col>
                <div className="d-flex align-items-center">
                  <XDiamond width="32" height="32" className="me-2" />
                  <div>
                    <div>New words</div>
                    <div className="fw-bold">
                      {newWords.length}{" "}
                      {newWords.length === 1 ? "word" : "words"}
                    </div>
                  </div>
                </div>
              </Col>
              <Col>
                <div className="d-flex align-items-center">
                  <XDiamond width="32" height="32" className="me-2" />
                  <div>
                    <div>Learning</div>
                    <div className="fw-bold">
                      {learningWords.length}{" "}
                      {learningWords.length === 1 ? "word" : "words"}
                    </div>
                  </div>
                </div>
              </Col>
              <Col>
                <div className="d-flex align-items-center">
                  <XDiamondFill width="32" height="32" className="me-2" />
                  <div>
                    <div>Learned</div>
                    <div className="fw-bold">
                      {learnedWords.length}{" "}
                      {learnedWords.length === 1 ? "word" : "words"}
                    </div>
                  </div>
                </div>
              </Col>
              <Col className="text-end">
                <Button
                  variant="outline-primary"
                  onClick={() => navigate("/exercises")}
                >
                  Train
                </Button>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Study Sets */}
      <Row className="d-flex m-0">
        {/* All Sets Button for xs and sm screens */}
        <Row className="mb-3 pe-0 align-items-center">
          <Col sm={6} xs={6} className="px-0">
            <h5 className="mb-0">Sets I'm learning</h5>
          </Col>
          <Col sm={6} xs={6} className="text-end px-0">
            <button
              className="btn btn-link"
              onClick={() => navigate("/allsets")}
            >
              See All Sets
            </button>
          </Col>
        </Row>
        <Row className="p-0">
          {/* My Vocabulary Column */}
          <Col md={2} sm={12} xs={12} className="pe-0 pb-2 pb-md-0">
            <Card
              className={`text-center h-100 mb-2 shadow-sm ${
                nightMode ? "bg-dark text-light " : "bg-light text-dark"
              }`}
              onClick={() => navigate("/vocabulary")}
              style={{ cursor: "pointer" }}
            >
              <Card.Body>
                <JournalText width="32" height="32" className="m-5" />
                <Card.Title className="font-weight-bold">
                  My Vocabulary
                </Card.Title>
                <Card.Text className="text-muted">
                  {totalWords} {totalWords === 1 ? "word" : "words"}
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
          {/* Dynamically Render User Sets */}
          {userSets.slice(0, 2).map((set) => (
            <Col
              md={4}
              sm={12}
              xs={12}
              className="d-flex justify-content-center pe-0 pb-2 pb-md-0"
              key={set.setName}
            >
              <div
                className="card-users-sets position-relative"
                onClick={() => handleViewSetWords(set.setName)}
              >
                <img
                  src={setsSignedUrls[set.id]}
                  className="card-img-top"
                  alt={set.setName}
                />
                <div className="card-img-overlay d-flex flex-column justify-content-end">
                  <h5 className="card-title text-white">{set.setName}</h5>
                  <p className="card-text text-white">
                    Words set · {set.words.length}{" "}
                    {set.words.length === 1 ? "word" : "words"}
                  </p>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </Row>
    </Container>
  );
}
